import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const FIELDS = [
  { id: "reg-user", name: "username", label: "Uživatelské jméno", type: "text", ph: "hrdina_faerunu", icon: "fa-user", maxLength: 17 },
  { id: "reg-email", name: "email", label: "E-mail", type: "email", ph: "ty@priklad.cz", icon: "fa-envelope", maxLength: 255 },
  { id: "reg-pass", name: "password", label: "Heslo", type: "password", ph: "••••••••", icon: "fa-lock", maxLength: 16 },
];

export const RegisterModal = ({ open, onClose }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const data = new FormData(e.target);
    const payload = {
      username: data.get("username").trim(),
      email: data.get("email").trim(),
      password: data.get("password"),
    };

    setSubmitting(true);
    try {
      await axios.post("/api/register", payload);
      toast("Účet byl vytvořen!", {
        description: "Teď se s ním můžeš přihlásit přímo do hry.",
      });
      e.target.reset();
      onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast("Uživatelské jméno je obsazené", {
          description: "Zkus zvolit jiné.",
        });
      } else if (status === 422) {
        const detail = err.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail[0]?.msg?.replace("Value error, ", "") : detail;
        toast("Zkontroluj zadané údaje", { description: msg || "Něco není v pořádku." });
      } else {
        toast("Registraci se nepodařilo dokončit", {
          description: "Zkus to prosím znovu za chvíli.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4"
          data-testid="register-modal"
        >
          <div
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            onClick={onClose}
            data-testid="register-modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-xl border border-white/10 bg-[#17171b] shadow-[0_20px_80px_rgba(0,0,0,0.7)]"
          >
            <button
              onClick={onClose}
              data-testid="register-modal-close"
              className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-md border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Zavřít"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-8 sm:p-10">
              <div className="mb-2 flex items-center gap-2">
                <i className="fa-solid fa-dragon text-[#F0801F]" />
                <span className="font-display text-xs uppercase tracking-[0.28em] text-[#F0801F]">
                  Draconia RP
                </span>
              </div>
              <h3 className="font-display text-2xl font-600 uppercase tracking-tight text-white">
                Vytvoř si účet
              </h3>
              <p className="mt-2 text-sm text-white/50">
                Připoj se ke světu Zapomenutých říší.
              </p>

              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                {FIELDS.map((f) => (
                  <div key={f.id}>
                    <label
                      htmlFor={f.id}
                      className="mb-2 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50"
                    >
                      {f.label}
                    </label>
                    <div className="relative">
                      <i className={`fa-solid ${f.icon} absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/30`} />
                      <input
                        id={f.id}
                        name={f.name}
                        type={f.type}
                        placeholder={f.ph}
                        maxLength={f.maxLength}
                        required
                        data-testid={f.id}
                        className="w-full rounded-md border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-white outline-none transition-all placeholder:text-white/25 focus:border-[#F0801F]/60 focus:bg-white/[0.06]"
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="register-submit-btn"
                  className="mt-2 w-full rounded-md bg-[#F0801F] py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Vytvářím účet…" : "Zaregistrovat se"}
                </button>
              </form>
              <p className="mt-4 text-center text-xs text-white/40">
                Heslo v klientovi hry nerozlišuje velká/malá písmena.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
